import { Loader2, Tag as TagIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.js';
import { Input } from '../../../components/ui/input.js';
import { useCreateTag, useUpdateTag } from '../../common/admin-hooks.js';

/**
 * Tag create / edit dialog. Mirrors the legacy
 * `wf-audit/src/slices/presentation/components/presentationFilter/ManageTags.tsx`
 * field set: title, description, color, icon. Replaces the new
 * SPA's title-only inline input. Operator-reported: "add new tag or
 * edit tag has more data in legacy system, like description, color,
 * icon, please do a proper popup for tags management".
 *
 * Two modes:
 *   - `create` — POST /v1/tags with the four fields.
 *   - `edit`   — PUT /v1/tags/{id} with the four fields (full
 *                replacement; engine schema is the same shape).
 *
 * Color + icon catalogue is a verbatim port of
 * `wf-audit/src/slices/presentation/constants/tagConstants.ts`:
 *   - 24 brand colors (HEX strings)
 *   - 76 named SVG glyphs served as `/img/tags/Icon_{N}.svg`
 * The 76 SVGs are copied into `apps/wf-coe-app-spa/public/img/tags/`
 * so existing tags imported from the legacy DB render with the same
 * icon they had pre-cutover.
 *
 * On success: closes itself + bubbles the created tag back via
 * `onSaved(tagId)` so the parent (the scenario tag selector) can
 * auto-attach the new tag.
 */
export interface TagEditorDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** When set, the dialog opens in edit mode; otherwise it's a fresh
   *  create. */
  initial?: {
    id: number;
    title: string;
    description: string | null;
    color: string | null;
    icon: string | null;
  } | null;
  /** Fired after a successful save with the new (or unchanged)
   *  tag id, so the caller can auto-attach the new tag, refresh a
   *  list, etc. */
  onSaved?: (tagId: number) => void;
}

const TAG_COLORS: ReadonlyArray<string> = [
  '#00C9C1', // Teal
  '#9C5BEF', // Purple
  '#FF852B', // Orange
  '#007CEE', // Blue
  '#D740CA', // Magenta
  '#464646', // Dark Gray
  '#FFD83A', // Yellow
  '#008F60', // Green
  '#EC4081', // Pink
  '#A8D73C', // Lime
  '#00C5E6', // Cyan
  '#FB3A30', // Red
  '#5670EF', // Light Blue
  '#0087B2', // Blue Gray
  '#00CB96', // Mint
  '#0056B2', // Navy
  '#E0B22E', // Gold
  '#765311', // Brown
  '#008C87', // Teal Dark
  '#A5B0FC', // Lavender
  '#702ECE', // Violet
  '#C2050C', // Crimson
  '#FF9C8B', // Salmon
  '#909090', // Gray
];

const TAG_ICONS: ReadonlyArray<string> = Array.from({ length: 76 }, (_, i) => `Icon_${i + 1}.svg`);

const DEFAULT_TAG_COLOR = TAG_COLORS[0]!;
const DEFAULT_TAG_ICON = TAG_ICONS[0]!;

export function TagEditorDialog({
  open,
  onOpenChange,
  initial,
  onSaved,
}: TagEditorDialogProps): JSX.Element {
  const isEdit = !!initial;
  const create = useCreateTag();
  const update = useUpdateTag();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_TAG_COLOR);
  const [icon, setIcon] = useState<string>(DEFAULT_TAG_ICON);

  // Re-seed form state each time the dialog opens so reopening a
  // partially-edited tag doesn't leak between sessions.
  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setColor(initial?.color ?? DEFAULT_TAG_COLOR);
    setIcon(initial?.icon ?? DEFAULT_TAG_ICON);
  }, [open, initial]);

  const saving = create.isPending || update.isPending;

  async function handleSave(): Promise<void> {
    const cleanTitle = title.trim();
    if (!cleanTitle) return;
    try {
      if (isEdit && initial) {
        await update.update({
          tagId: initial.id,
          title: cleanTitle,
          description: description.trim() || null,
          color,
          icon,
        });
        onSaved?.(initial.id);
      } else {
        const result = (await create.create({
          title: cleanTitle,
          description: description.trim() || null,
          color,
          icon,
        })) as { tagId?: number } | undefined;
        if (typeof result?.tagId === 'number') {
          onSaved?.(result.tagId);
        }
      }
      onOpenChange(false);
    } catch {
      // Hooks surface the error via toast; dialog stays open.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <TagIcon className="h-4 w-4" />
            {isEdit ? `Edit "${initial?.title ?? 'tag'}"` : 'New tag'}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <label htmlFor="tag-title" className="text-xs font-medium text-foreground/70">
              Title <span className="text-rose-500">*</span>
            </label>
            <Input
              id="tag-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
              placeholder="e.g. Operations"
              className="border-foreground/15 bg-foreground/5"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="tag-description" className="text-xs font-medium text-foreground/70">
              Description
            </label>
            <textarea
              id="tag-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional — what does this tag group?"
              className="w-full rounded-md border border-foreground/15 bg-foreground/5 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/40"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground/70">Color</label>
            {/* 24-color brand palette ported from legacy tagConstants.ts. */}
            <div className="grid grid-cols-12 gap-1.5">
              {TAG_COLORS.map((swatch) => {
                const active = color === swatch;
                return (
                  <button
                    key={swatch}
                    type="button"
                    onClick={() => setColor(swatch)}
                    aria-label={`Select ${swatch}`}
                    aria-pressed={active}
                    className={`h-5 w-5 rounded-full border-2 transition-transform ${
                      active ? 'scale-110 border-foreground' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: swatch }}
                  />
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground/70">Icon</label>
            {/* 76-icon legacy catalogue, served from public/img/tags/.
                Two-row scrollable grid keeps the dialog vertically
                compact; selected icon gets a yellow ring + light tint
                so picker state is unambiguous regardless of glyph
                colour. */}
            <div className="grid max-h-40 grid-cols-12 gap-1.5 overflow-y-auto rounded-md border border-foreground/10 bg-foreground/5 p-1.5">
              {TAG_ICONS.map((name) => {
                const active = icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setIcon(name)}
                    aria-label={`Select icon ${name}`}
                    aria-pressed={active}
                    title={name}
                    className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
                      active
                        ? 'bg-foreground/15 ring-2 ring-[color:var(--brand-yellow)]'
                        : 'hover:bg-foreground/10'
                    }`}
                  >
                    <img
                      src={`/img/tags/${name}`}
                      alt={name}
                      className="h-4 w-4"
                      onError={(e) => {
                        // Hide missing icon assets silently — fall back
                        // to the file-name as a tooltip.
                        (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={!title.trim() || saving}
            className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
          >
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Create tag'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
